<script setup>
import { computed } from 'vue';

const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value),
  },
  schoolType: { 
    type: String,
    required: true,
    validator: (value) => ['elementary', 'middle', 'high'].includes(value),
  },
  grades: {
    type: [Array, String],
    default: () => []
  }
});
const scoreData = {
    literacy: {
        '4학년': {levels:[{level:1,range:'1000~1226'},{level:2,range:'1227~1353'},{level:3,range:'1354~1526'},{level:4,range:'1527~1700'},],},
        '6학년': {levels:[{level:1,range:'1050~1320'},{level:2,range:'1321~1432'},{level:3,range:'1433~1594'},{level:4,range:'1595~1800'},],},
        '중2학년': {levels:[{level:1,range:'1100~1412'},{level:2,range:'1413~1529'},{level:3,range:'1530~1665'},{level:4,range:'1666~1900'},],},
        '고1학년': {levels:[{level:1,range:'1150~1534'},{level:2,range:'1535~1622'},{level:3,range:'1623~1739'},{level:4,range:'1740~2000'},],},
    },
    numeracy: {
        '4학년': {levels:[{level:1,range:'1000~1225'},{level:2,range:'1226~1341'},{level:3,range:'1342~1449'},{level:4,range:'1450~1700'},],},
        '6학년': {levels:[{level:1,range:'1050~1294'},{level:2,range:'1295~1387'},{level:3,range:'1388~1541'},{level:4,range:'1542~1800'},],},
        '중2학년': {levels:[{level:1,range:'1100~1387'},{level:2,range:'1388~1500'},{level:3,range:'1501~1620'},{level:4,range:'1621~1900'},],},
        '고1학년': {levels:[{level:1,range:'1150~1435'},{level:2,range:'1436~1573'},{level:3,range:'1574~1696'},{level:4,range:'1697~2000'},],},
    },
};

const targetGradeForCard = computed(() => {
    if (props.schoolType === 'elementary') {
        // grades prop이 있으면 실제 데이터를 사용, 없으면 기본값
        if (props.grades && props.grades.length > 0) {
            const gradeArray = Array.isArray(props.grades) ? props.grades : [props.grades];
            return gradeArray.map(grade => `${grade}학년`).sort();
        }
        return ['4학년', '6학년']; // 기본값
    } else if (props.schoolType === 'middle') {
        return ['중2학년']; 
    } else if (props.schoolType === 'high') {
        return ['고1학년']; 
    }
    return []; // 기본값은 빈 배열
});

const achievementData = {
  literacy: {
    '4학년': [
      { level: 4, performances: ['맥락을 고려하여 어휘의 의미를 추론하고 단어 간 관계를 파악하여 적용할 수 있다.','새로운 정보나 사회 문제를 다루는 다양한 글을 읽고 세부 내용을 파악하여 글의 구조에 따라 내용을 간추릴 수 있다.','단서나 배경지식을 활용하여 생략된 내용을 추론할 수 있다.','다양한 자료를 해석하여 새로운 상황의 문제를 해결할 수 있다.'] },
      { level: 3, performances: ['맥락을 고려하여 어휘의 의미를 이해하고 단어 간 관계를 파악할 수 있다.','글쓴이의 의도나 글의 목적을 고려하여 중심 내용을 파악하고 간추릴 수 있다.','일상 소재를 다룬 다양한 매체의 세부 내용을 이해하여 생략된 내용을 추론할 수 있다.','글, 그림, 도표 등의 자료를 분석하여 문제를 해결할 수 있다.'] },
      { level: 2, performances: ['일상에서 자주 접하는 어휘의 의미를 이해할 수 있다.','일상적이고 친숙한 소재를 다룬 글의 주요 내용을 파악할 수 있다.','글, 그림, 도표 등의 자료에서 명확하게 드러난 정보를 찾을 수 있다.','글에 드러난 단서를 통해 생략된 내용을 짐작할 수 있다.','글의 내용을 활용하여 일상생활의 단순한 문제를 해결할 수 있다.'] }, 
      { level: 1, performances: ['일상에서 자주 쓰는 단어의 뜻을 대략 이해할 수 있다.','생활에서 친숙한 소재의 글을 읽고 대강의 내용을 확인할 수 있다.','이야기 속 인물의 말이나 행동을 파악할 수 있으며 이어질 내용을 짐작할 수 있다.','짧은 글을 읽고 자기 생각이나 느낌을 표현할 수 있다.'] }
    ],
    '6학년': [
      { level: 4, performances: ['어휘의 사전적 의미를 정확히 알고 학습과 삶의 맥락에서 정교하게 활용할 수 있다.','다양한 자료를 분석하여 세부 내용을 정확하게 파악할 수 있다.','폭넓은 주제의 글을 읽고 생략된 정보를 추론하여 해석할 수 있다.','글의 내용과 글쓴이의 의도를 비판적으로 판단하고 삶과 연계된 문제에 적용하여 적절한 대안을 제시할 수 있다.'] },
      { level: 3, performances: ['어휘의 맥락적 의미를 이해하고 상황에 따라 다양하게 적용할 수 있다.','그림이나 도표가 포함된 자료를 분석하여 필요한 정보를 선별할 수 있다.','자료에 포함된 글이나 그림 등을 단서로 활용하여 생략된 부분을 추론할 수 있다.','글의 내용과 글쓴이의 의도를 판단하고 문제 해결을 위한 적절한 방법을 찾을 수 있다.'] },
      { level: 2, performances: ['맥락을 살펴 어휘의 의미를 알맞게 짐작할 수 있다.','친숙한 소재를 다룬 자료를 읽고 주요 정보를 찾거나 중심 내용을 파악할 수 있다.','자료에 포함된 글이나 그림 등을 바탕으로 글에 드러나지 않은 내용을 예상할 수 있다.','글의 내용과 글쓴이의 의도를 판단할 수 있다.'] },
      { level: 1, performances: ['일상생활에서 자주 쓰는 어휘의 뜻을 이해할 수 있다.','그림이나 글에서 명확하게 드러난 정보를 찾을 수 있다.','친숙한 소재를 다룬 간단한 글을 읽고 중심 내용을 파악할 수 있다.','짧은 이야기 속 인물의 말과 행동을 통해 생각과 감정을 짐작할 수 있다.'] }
    ],
    '중2학년': [
      { level: 4, performances: ['일상과 학습 상황에서 사용되는 추상적인 어휘를 활용할 수 있다.','다양한 분야의 글을 읽고, 배경지식을 활용하여 생략된 정보를 추론할 수 있다.','글의 다양한 자료를 통합하여 종합적으로 이해할 수 있다. 관점의 차이를 파악하고 의견의 타당성을 평가할 수 있다.','글의 내용을 새로운 상황에 적용하여 문제를 해결할 수 있다.'] },
      { level: 3, performances: ['학습에 필요한 어휘의 의미와 쓰임을 이해할 수 있다.','일상생활에서 사용하는 단어의 의미 관계를 파악할 수 있다.','글의 내용을 문단별로 요약할 수 있다.','다양한 종류의 글을 읽고, 세부 내용을 바탕으로 복합자료의 의미를 해석할 수 있다.','글에서 생략된 내용을 추론할 수 있다.','글의 내용을 주어진 상황에 적용할 수 있다.'] },
      { level: 2, performances: ['일상생활에서 사용하는 어휘의 의미를 알고 상황에 맞게 사용할 수 있다.','글에 제시된 사실적인 정보를 찾아내고 이해할 수 있다.','사진이나 그림, 카드뉴스 등의 자료를 활용해서 관련된 글의 내용을 해석할 수 있다.','쉽게 접할 수 있는 생활문제에 관한 글을 읽고 글쓴이의 의도를 파악할 수 있다.'] },
      { level: 1, performances: ['자주 쓰는 어휘나 쉬운 관용 표현의 의미를 알 수 있다.','일상적 어휘로 표현된 글을 읽고 내용을 파악할 수 있다.','짧은 이야기 글의 중심 내용을 찾을 수 있다.','관심 있는 분야나 그림, 사진이 포함된 정보 글을 읽고 필요한 정보를 파악할 수 있다.'] }
    ],
    '고1학년': [
      { level: 4, performances: ['글의 맥락을 고려하여 추상적·전문적인 어휘의 의미를 추론할 수 있다.','과학·기술 및 사회·경제 분야의 글을 읽고 세부 내용을 정확하게 파악하고 통합하거나 일반화할 수 있다.','관점이 다른 글이나 도표, 통계 등을 비교·분석하여 비판적으로 평가할 수 있다.','다양한 유형의 글을 읽고 전개 방식이나 서술 방법을 파악할 수 있다.'] },
      { level: 3, performances: ['글의 맥락을 고려하여 추상적인 어휘의 의미를 이해할 수 있다.','과학·기술 및 사회·경제 분야의 글을 읽고 제시된 자료를 바탕으로 드러나지 않은 정보를 추론할 수 있다.','추가적인 정보가 있는 그래프나 도표 등의 자료를 글과 연결 지어 해석할 수 있다.','다양한 관점이 포함된 글을 읽고 관점의 차이를 파악할 수 있다.'] },
      { level: 2, performances: ['글의 맥락을 고려하여 일상적 어휘나 관용 표현의 의미를 이해하고 사용할 수 있다.','일상과 밀접한 사회·과학 분야의 글에서 명시적으로 드러난 정보를 파악하고 중심 내용을 찾을 수 있다.','도표나 이미지 등 다양한 시각 자료를 활용한 글의 핵심 내용을 이해할 수 있다.'] },
      { level: 1, performances: ['일상적인 어휘나 쉬운 관용 표현의 의미를 이해하고, 맥락을 통해서 단어나 어구의 의미를 짐작할 수 있다.','일상과 밀접한 글을 읽고 글의 목적을 파악할 수 있다.','시각 자료에 담긴 정보를 찾을 수 있다.','이야기 글을 읽고 인물이 처한 상황을 파악할 수 있다.'] }
    ]
  },
  numeracy: {
    '4학년': [
      { level: 4, performances: ['수리적 사고를 활용해 일상생활 속 다양한 상황의 해결책을 찾을 수 있다.','도형의 특징을 이해하고 그 관계를 탐구하며, 길이, 무게, 들이와 같은 단위를 상황에 맞게 적용하여 문제를 해결할 수 있다.','주어진 식이나 도형의 복잡한 배열에서 규칙을 추론해 결과를 예측하고, 자료를 분석해 표와 그래프로 나타내고 이해할 수 있다.'] },
      { level: 3, performances: ['수에 대한 개념을 알고 있으며, 사칙연산을 활용하여 생활 속 다양한 상황을 해결할 수 있다.', '주어진 상황에 도형의 기본 개념을 활용할 수 있고, 무게와 들이 단위를 사용해 합과 차를 구하는 문제를 해결할 수 있다.', '주어진 식이나 도형의 배열에서 규칙과 관계를 찾을 수 있으며, 자료를 분석해 추론하고 그림그래프로 나타낼 수 있다.'] },
      { level: 2, performances: ['일상생활에서 접하는 간단한 문제를 식으로 만들고 계산할 수 있다.','도형의 기본 개념을 이해하고 있으며, 식이나 도형으로 나타낸 간단한 배열에서 변화와 규칙을 찾아낼 수 있다.','직각, 예각, 둔각의 개념을 알고 구별할 수 있으며, 간단한 무게의 차를 계산할 수 있다.','표와 그림에서 필요한 정보를 찾을 수 있다.'] },
      { level: 1, performances: ['일상생활 속 시각 자료, 그림 등에 제시된 단순한 계산을 할 수 있다.','배열에 있는 규칙을 일부 찾아낼 수 있으며, 그림에서 직각과 비교하여 예각과 둔각을 구별할 수 있다.','제시된 자료를 간단한 기준에 따라 분류할 수 있다.'] },
    ],
    '6학년': [
      { level: 4, performances:['다양한 상황 속 수학적 요소를 종합적으로 분석하여 문제를 해결할 수 있다.','최대공약수와 최소공배수를 활용하여 실생활 문제를 해결할 수 있다.','추론을 통해 비율과 가능성을 수학적으로 표현할 수 있다.','평면도형의 넓이를 구하고 크기를 비교할 수 있다.','다양한 데이터를 해석하여 중요한 정보를 파악하고, 이를 바탕으로 의사소통할 수 있다.'] },
      { level: 3, performances: ['수와 도형의 개념을 활용하여 일상생활 속 다양한 분야의 문제를 해결할 수 있다.','평균의 의미를 알고 일상생활에서 평균을 활용할 수 있다.','문제 상황에서 두 수의 대응 관계를 식으로 나타낼 수 있다.','그래프를 보고 필요한 정보를 파악할 수 있다.','어림이 필요한 상황에서 어림값을 구할 수 있다.'] },
      { level: 2, performances: ['일상생활 속 그림, 그래프, 표 등에 제시된 수와 도형의 개념을 이해할 수 있다.','문제 상황에 맞는 식을 만들고, 자연수의 혼합 계산으로 실생활 문제를 해결할 수 있다.','기초적인 수학적 추론을 통해 반복되는 배열과 규칙을 찾아 문제를 해결할 수 있다.','일상생활에서 접하는 입체도형의 구성 요소를 찾을 수 있다.'] },
      { level: 1, performances: ['일상생활에서 접하는 안내문이나 시각 자료에 있는 단순한 수와 도형의 개념을 이해할 수 있다.','간단한 자연수의 혼합 계산이 가능하고 상황에 맞는 식을 세울 수 있다.','수 배열에서 규칙을 찾고 다음 수를 예측할 수 있다.','분류된 자료를 표로 나타내고 자룟값을 비교할 수 있다.'] },
    ],
    '중2학년': [
      { level: 4, performances: ['여러 분야에서 적절한 수학적 개념과 방법, 추론을 통해 복합적인 문제를 효과적으로 해결할 수 있다.','올바른 수학적 표현과 수리적 사고를 바탕으로 의사소통할 수 있다.','새로운 상황에서 수학적 구조를 파악하여 다양한 형태로 제시된 정보를 해석하고 종합 할 수 있으며, 이에 근거하여 합리적인 의사결정을 할 수 있다.'] },
      { level: 3, performances: ['일상생활 맥락과 학습 상황에서 수학적 표현을 이해하고, 수학적 개념과 방법을 활용하여 문제를 해결할 수 있다.','다양한 시각 자료와 변화하는 양 사이의 관계를 통해 정보를 파악하고 상황에 맞는 수학적 지식을 적용할 수 있다.','제시된 수학적 정당화 과정을 이해하고 필요한 조건을 판단할 수 있다.'] },
      { level: 2, performances: ['일상생활 맥락과 학습 상황에서 수학적 지식과 방법을 통해 간단한 문제를 해결할 수 있다.','유리수의 사칙계산을 할 수 있고 문자를 사용한 식을 이해할 수 있으며 기본적인 방정식을 풀 수 있다.','그림을 보고 도형을 구분하고, 단순한 도형의 넓이나 부피를 구할 수 있다.','간단한 표와 그래프를 해석할 수 있다.'] },
      { level: 1, performances: ['익숙한 일상생활 맥락과 학습 상황 속에서 자기 경험을 토대로 수와 도형에 대한 기초적인 개념을 이해할 수 있다.','증가와 감소, 영상과 영하 등 자신의 생활과 밀접한 상황을 양수와 음수로 표현할 수 있다.','시각 자료를 바탕으로 간단한 식의 계산을 할 수 있고 주변에서 볼 수 있는 사물을 간단한 도형으로 나타낼 수 있다.'] }
    ],
    '고1학년': [
      { level: 4, performances: ['다양한 수학적 개념을 활용하여 공학·인문 등의 분야에서 나타난 상황을 해석하고, 복잡한 계산과 수학적 추론을 할 수 있다.','변화하는 상황을 이해하여 관계를 식, 표, 그래프 등의 적절한 수학적 표현으로 나타내고 문제를 해결할 수 있다.','자료에서 얻은 정보 간의 관계를 파악하고 수학적 근거를 바탕으로 의사소통할 수 있다.'] },
      { level: 3, performances: ['일상생활 맥락과 학습 상황에서 실수의 대소 관계를 이해하고, 다항식의 계산을 할 수 있다.','곱셈공식과 인수분해, 이차함수와 이차방정식의 관계 및 도형의 방정식을 활용하여 다양한 문제를 해결할 수 있다.','확률의 뜻을 알고 자료를 맞게 해석하여 의사결정을 할 수 있다.'] },
      { level: 2, performances: ['일상생활 맥락과 학습 상황에서 집합의 연산과 제곱근의 계산을 할 수 있다.','단순한 자료를 비교하여 정보를 파악하고, 행렬의 연산을 할 수 있다.','변화하는 두 양 사이의 규칙을 찾을 수 있다.','간단한 도형의 성질을 이용하여 문제를 해결할 수 있다.','합의 법칙 또는 곱의 법칙을 이용하여 간단한 경우의 수를 구할 수 있다.'] },
      { level: 1, performances: ['익숙한 일상생활 맥락에서 정수와 유리수의 간단한 계산을 할 수 있다.','문자를 사용한 식의 의미를 부분적으로 이해할 수 있다.','도형에 대한 단순한 개념과 성질을 안다.','자료를 통해 정보를 일부 찾을 수 있고, 간단한 경우의 수를 셀 수 있다.'] }
    ]
  },
};

const scoreCardTitle = computed(() => {
  return props.type === 'literacy' ? '문해력 척도 점수와 수준' : '수리력 척도 점수와 수준';
});


const gradesData=computed(()=>{
    const selectedScoreData = targetGradeForCard.value.map(grade => {
        return {
            grade: grade, 
            levels: scoreData[props.type][grade] ? scoreData[props.type][grade].levels : []
        };
    });
    return selectedScoreData.filter(item => item.levels.length > 0); 
});


const allGradesData=computed(()=>{
    const selectedAchievementData = {};
    targetGradeForCard.value.forEach(grade => {
        if (achievementData[props.type][grade]) {
            selectedAchievementData[grade] = achievementData[props.type][grade];
        }
    });
    return selectedAchievementData;
});
</script>

<template>  
    <div class="card" :class="[props.type, props.schoolType]">
      <h5 class="card-title" :class="props.type">{{ scoreCardTitle }}</h5>
      <div class="step-wrap pt-4" :class="{ 'single-grade': gradesData.length === 1 }">
        <div class="step" v-for="(gradeInfo, index) in gradesData" :key="index">
          <p>{{ gradeInfo.grade }}</p>
          <ul>
            <li v-for="(levelInfo, levelIndex) in gradeInfo.levels" :key="levelIndex">
              <p>{{ levelInfo.level }} 수준</p>
              <p>{{ levelInfo.range }}</p>
            </li>
          </ul>
        </div>
      </div>
      <p class="caution pt-4">학년별 척도 점수의 범위는 다르며(초4: 1000~1700점, 초6: 1050~1800점, 중2: 1100~1900점, 고1: 1150~2000점), 학년별 수준은 북마크 기준설정을 통해 도출된 분할점수를 바탕으로 네 개의 수준으로 구분한 것입니다. 4수준이 가장 높은 수준을 나타내며 1수준은 가장 낮은 수준입니다.</p>
    </div>    
    <div class="card pt-[30px]" :class="[props.type, props.schoolType]">
      <h5 class="card-title" :class="props.type">학년별 각 수준에서 기대되는 성취 수행</h5>
      <div class="table-wrap pt-5">
        <table v-for="(gradePerformances, gradeKey) in allGradesData" :key="gradeKey">
            <colgroup>
            <col style="width: 10%" />
            <col style="width: 90%" />
            </colgroup>
          <thead>
            <tr>
              <th>수준</th>
              <th><span class="point">{{ gradeKey }}</span><span class="pl-2"> 기대 성취 수행</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="gradePerformances.length === 0">
              <td colspan="2">{{ gradeKey }}의 기대 성취 수행 데이터가 없습니다.</td>
            </tr>
            <template v-else>
              <tr v-for="(item, itemIndex) in gradePerformances" :key="itemIndex">
                <td>{{ item.level }}</td>
                <td>
                  <p v-for="(performance, pIndex) in item.performances" :key="pIndex">
                    {{ performance }}
                  </p>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p class="caution pt-4">각 성취 수준에 있는 기술은 해당 수준에 있는 학생이 ‘확률적’으로 수행할 것으로 기대되는 내용을 제시한 것이며, 각 학생이 실제로 성취 수준에 있는 내용을 수행했다는 것을 의미하지는 않습니다.</p>
    </div>  
</template>

<style lang="scss" scoped>
.step-wrap {
    display: flex;
    justify-content: space-between;
    &.single-grade {
        justify-content: center;
    }
    .step {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 8px;
        > p {
            color: #fff;
            border-radius: 8px;
            width: 88px;
            font-size: 20px;            
            line-height: 26px;
        }
        ul {
            display: flex;
            border: 2px solid #000;
            border-radius: 10px;
            font-weight: 500;
            li {
                height: 78px;
                width: 125px;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 4px;
                p:nth-child(2) {
                    font-size: 16px;
                    display: inline-block;
                    padding: 4px 8px;
                    background-color: #fff;
                    border: 1px solid rgba( var(--result-light-blue-rgb),.2);
                    border-radius: 50px;
                }
            }
        }
        &:nth-child(2) {
             > p {background-color: #00A496; }
             ul {
                @for $i from 1 through 4 {
                    li:nth-child(#{$i})   {
                        $opacity: 0; // 기본 투명도 초기화
                        @if $i == 1 {
                            $opacity: 0.05;
                        } @else if $i == 2 {
                            $opacity: 0.1;
                        } @else if $i == 3 {
                            $opacity: 0.2;
                        } @else if $i == 4 {
                            $opacity: 0.3;
                        }
                        background-color: rgba(0, 164, 150, $opacity);
                    }
                }
            }
        }
    } 
}

.literacy {
    .step {
        > p {background-color: var(--result-light-blue);}
        ul {
            @for $i from 1 through 4 {
                li:nth-child(#{$i})   {
                    $opacity: 0; 
                    @if $i == 1 {
                        $opacity: 0.05;
                    } @else if $i == 2 {
                        $opacity: 0.1;
                    } @else if $i == 3 {
                        $opacity: 0.2;
                    } @else if $i == 4 {
                        $opacity: 0.3;
                    }
                    background-color: rgba(var(--result-light-blue-rgb), $opacity);
                }
            }
        }
    }
    .table-wrap table {    
        &:nth-child(1) {
          .point {
            background-color: #6484F0;
          }
        }  
        th:nth-child(1) { background-color: rgba(var(--result-light-blue-rgb), .8);}
        @for $i from 1 through 4 {
            tr:nth-child(#{$i}) td:nth-child(1) {
                $opacity: 0; 
                @if $i == 1 {
                    $opacity: 0.3;
                } @else if $i == 2 {
                    $opacity: 0.2;
                } @else if $i == 3 {
                    $opacity: 0.1;
                } @else if $i == 4 {
                    $opacity: 0.05;
                }                
                background-color: rgba(var(--result-light-blue-rgb), $opacity);
            }
        }
    }
}
.numeracy {
    .step {
        > p {background-color: var(--result-light-red);}
        ul {
            @for $i from 1 through 4 {
                li:nth-child(#{$i})   {
                    $opacity: 0; 
                    @if $i == 1 {
                        $opacity: 0.05;
                    } @else if $i == 2 {
                        $opacity: 0.1;
                    } @else if $i == 3 {
                        $opacity: 0.2;
                    } @else if $i == 4 {
                        $opacity: 0.3;
                    }
                    background-color: rgba(var(--result-light-red-rgb), $opacity);
                }
            }
        }
    }
    .table-wrap table {   
        &:nth-child(1) {
          .point {
            background-color: #F88087;
          }
        }     
        th:nth-child(1) { background-color: rgba(var(--result-light-red-rgb), .8);}
        @for $i from 1 through 4 {
            tr:nth-child(#{$i}) td:nth-child(1) {
                $opacity: 0; 
                @if $i == 1 {
                    $opacity: 0.3;
                } @else if $i == 2 {
                    $opacity: 0.2;
                } @else if $i == 3 {
                    $opacity: 0.1;
                } @else if $i == 4 {
                    $opacity: 0.05;
                }                
                background-color: rgba(var(--result-light-red-rgb), $opacity);
            }
        }
    }
}

.table-wrap {
    display: flex;
    gap: 20px;
    table {
            height: 740px;
        th {
            font-size: 20px;
            font-weight: 500;
            &:nth-child(1) {color: #fff; font-weight: 400;} 
            &:nth-child(2) {
                background-color: #FAFAFA;
            }
        }
        td {
            height: 140px;
            &:nth-child(1) {
                font-size: 32px;
                font-weight: 500;
                text-align: center;
            }
            &:nth-child(2) {
                font-size: 17px;               
                padding: 4px 8px 4px 12px;
                p {
                    position: relative;
                    padding-left: 10px;
                    &::before {
                        content: '';
                        position: absolute;
                        width: 3px;
                        height: 3px;
                        background-color: #000;
                        border-radius: 100%;
                        left: 0;
                        top: 9px;  
                    }
                }
            }
        }        
        &:nth-child(2) {
            .point {background-color: #00A496;}
            th:nth-child(1) { background-color: rgba(0, 164, 150, .8);}
            @for $i from 1 through 4 {
                tr:nth-child(#{$i}) td:nth-child(1) {
                    $opacity: 0; 
                    @if $i == 1 {
                        $opacity: 0.3;
                    } @else if $i == 2 {
                        $opacity: 0.2;
                    } @else if $i == 3 {
                        $opacity: 0.1;
                    } @else if $i == 4 {
                        $opacity: 0.05;
                    }                
                    background-color: rgba(0, 164, 150, $opacity);
                }
            }
        }
    }
}
.point {
  border-radius: 8px;
  width: 88px;
  font-size: 20px;
  line-height: 1.4;
  color: #fff;
  display: inline-block;
}

.middle, .high {
  .step-wrap, .step, .step ul {width: 100%;}
  .step-wrap .step ul li {width: 25%;}
  .table-wrap table { height: 700px;}  
  .table-wrap table td:nth-child(2) {font-size: 18px; line-height: 1.5;}
}

.elementary {
  .step-wrap.single-grade {
    .step, .step ul {width: 100%;}
    .step ul li {width: 25%;}
  }
}

</style>